import {
  Button, IconButton, List, ListItem, ListSubheader, TextField, Tooltip, Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { faCrosshairs, faTrashAlt, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { LineStatus, MarkdownLine } from 'services/imports/markdownContract';
import type { LineSnapshot } from 'views/imports/doc-model';
import { StatusChip, StatusTone } from 'views/imports/components/status-chip';
import { humanKeyLabel } from 'views/imports/key-labels';
import { matchesFilter, relatedFilterFor } from 'views/imports/row-filter';
import type { AmendmentRow } from 'views/imports/use-imports-state';

/** Chip tone per line status; null means the line is healthy and shows its source instead. */
const chipToneFor = (status: LineStatus): StatusTone | null => {
  switch (status) {
    case 'ok':
      return null;
    case 'unknown-key':
    case 'unverified':
      return 'warning';
    case 'phase-2':
      return 'info';
    case 'malformed':
      return 'error';
    case 'manual':
      return 'neutral';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
};

/** Statuses that need a reviewer's attention before sending. */
const FLAGGED: ReadonlySet<LineStatus> = new Set<LineStatus>(['unverified', 'malformed', 'unknown-key']);

const familyOf = (line: MarkdownLine): string => (line.key === '' ? 'other' : line.key.split('.')[0]);

const rowDomId = (line: MarkdownLine): string => (line.id ? `import-row-${line.id}` : `import-row-line-${line.lineNo}`);

type Group = { family: string; lines: MarkdownLine[] };

/** Groups keep document order inside each family; families appear in the order they first occur. */
const groupByFamily = (lines: readonly MarkdownLine[]): Group[] => {
  const groups = new Map<string, MarkdownLine[]>();
  lines.forEach((line) => {
    const family = familyOf(line);
    groups.set(family, [...(groups.get(family) ?? []), line]);
  });
  return Array.from(groups.entries()).map(([family, grouped]) => ({ family, lines: grouped }));
};

/** Columns shared by every row, so the value and each icon line up vertically across the list. */
const ACTION_SLOT = 4;
const useStyles = makeStyles((theme: Theme) => createStyles({
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  group: {
    backgroundColor: theme.palette.background.paper,
    lineHeight: 'normal',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: `minmax(0, 12rem) minmax(0, 1fr) ${theme.spacing(ACTION_SLOT * 3)}`,
    columnGap: theme.spacing(1.5),
    rowGap: theme.spacing(0.5),
    alignItems: 'center',
    padding: theme.spacing(1, 1, 1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderLeft: '3px solid transparent',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: `minmax(0, 1fr) ${theme.spacing(ACTION_SLOT * 3)}`,
    },
  },
  selected: {
    borderLeftColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  label: {
    gridColumn: 1,
    minWidth: 0,
    overflowWrap: 'anywhere',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
    },
  },
  value: {
    gridColumn: 2,
    minWidth: 0,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      gridColumn: 1,
      gridRow: 2,
    },
  },
  actions: {
    gridColumn: 3,
    display: 'flex',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      gridColumn: 2,
      gridRow: 2,
    },
  },
  slot: {
    width: theme.spacing(ACTION_SLOT),
    height: theme.spacing(ACTION_SLOT),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  malformed: {
    gridColumn: '1 / span 2',
    overflowWrap: 'anywhere',
    [theme.breakpoints.down('sm')]: {
      gridColumn: '1 / -1',
    },
  },
  // Under the input: the source on the left, status chips right-aligned.
  meta: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  chips: {
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  suggestion: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    margin: -1,
    padding: 0,
    border: 0,
  },
  empty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  // Description on the left, restore pinned to the right on the same line.
  // MUI v4 buttons uppercase their text by default; the list keeps the copy as written.
  button: {
    textTransform: 'none',
  },
  sourceLink: {
    minWidth: 0,
    padding: 0,
    fontSize: theme.typography.caption.fontSize,
  },
  removedRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1, 1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  removedText: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    overflowWrap: 'anywhere',
  },
  restore: {
    flexShrink: 0,
    marginLeft: 'auto',
  },
}));

type LineRowProps = {
  line: MarkdownLine;
  original: LineSnapshot | undefined;
  selected: boolean;
  focusInput: boolean;
  amendments: readonly AmendmentRow[];
  disabled: boolean;
  onSelect: (id: string) => void;
  onChange: (id: string, value: string) => void;
  onReset: (id: string) => void;
  onRemove: (line: MarkdownLine) => void;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
  onShowOnly: (key: string) => void;
  onOpenSource?: () => void;
};

const LineRow = ({
  line, original, selected, focusInput, amendments, disabled, onSelect, onShowOnly, onOpenSource, onChange, onReset, onRemove, onAccept, onDismiss,
}: LineRowProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const isMalformed = line.status === 'malformed';
  const id = line.id ?? '';
  const edited = original !== undefined && !isMalformed && original.value !== line.value;
  const tone = chipToneFor(line.status);

  useEffect(() => {
    if (focusInput) inputRef.current?.focus();
  }, [focusInput]);

  const slot = (content: React.ReactNode) => (
    <span className={classes.slot}>{content}</span>
  );

  return (
    <ListItem
      id={rowDomId(line)}
      disableGutters
      className={`${classes.row} ${selected ? classes.selected : ''}`}
      aria-current={selected ? 'true' : undefined}
      data-line-status={line.status}
    >
      {isMalformed ? (
        <Typography variant="body2" className={classes.malformed}>{line.raw}</Typography>
      ) : (
        <>
          <Typography variant="body2" className={classes.label} title={line.key}>{humanKeyLabel(line.key)}</Typography>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            value={line.value}
            disabled={disabled}
            className={classes.value}
            onFocus={() => onSelect(id)}
            onChange={(event) => onChange(id, event.target.value)}
            inputRef={inputRef}
            inputProps={{ 'aria-label': t('imports:rows.valueFor', { key: humanKeyLabel(line.key) }) }}
          />
        </>
      )}
      <div className={classes.actions}>
        {isMalformed ? slot(null) : slot(
          <Tooltip title={line.src ? t('imports:rows.showSource') : t('imports:rows.noSource')}>
            <span>
              <IconButton
                size="small"
                disabled={line.src === null || disabled}
                aria-label={t('imports:rows.showSourceFor', { key: humanKeyLabel(line.key) })}
                onClick={() => {
                  onShowOnly(relatedFilterFor(line.key));
                  onSelect(id);
                  onOpenSource?.();
                }}
              >
                <FontAwesomeIcon icon={faCrosshairs} size="sm" />
              </IconButton>
            </span>
          </Tooltip>,
        )}
        {slot(edited ? (
          <Tooltip title={t('imports:rows.originalValue', { value: original?.value ?? '' })}>
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                aria-label={t('imports:rows.resetFor', { key: humanKeyLabel(line.key) })}
                onClick={() => onReset(id)}
              >
                <FontAwesomeIcon icon={faUndo} size="sm" />
              </IconButton>
            </span>
          </Tooltip>
        ) : null)}
        {slot(
          <IconButton
            size="small"
            disabled={disabled}
            aria-label={t('imports:rows.removeFor', { key: humanKeyLabel(line.key) || line.raw })}
            onClick={() => onRemove(line)}
          >
            <FontAwesomeIcon icon={faTrashAlt} size="sm" />
          </IconButton>,
        )}
      </div>
      {(line.src && !isMalformed) || tone !== null || edited ? (
        <div className={classes.meta}>
          {line.src && !isMalformed && (
            <Button
              size="small"
              color="primary"
              className={`${classes.button} ${classes.sourceLink}`}
              aria-label={t('imports:rows.showOnlyFor', { key: humanKeyLabel(line.key) })}
              onClick={() => {
                onShowOnly(relatedFilterFor(line.key));
                onSelect(id);
              }}
            >
              {t('imports:editor.source', { page: line.src.page, line: line.src.line })}
            </Button>
          )}
          <div className={classes.chips}>
            {tone !== null && <StatusChip label={t(`imports:badge.${line.status}`)} tone={tone} />}
            {edited && <StatusChip label={t('imports:rows.edited')} tone="info" />}
          </div>
        </div>
      ) : null}
      {amendments.map((row) => (
        <div key={row.id} className={classes.suggestion}>
          <Typography variant="body2">{`${t('imports:amendments.suggested')}: ${row.value}`}</Typography>
          <Button size="small" color="primary" className={classes.button} disabled={disabled} onClick={() => onAccept(row.id)}>
            {t('imports:amendments.accept')}
          </Button>
          <Button size="small" className={classes.button} disabled={disabled} onClick={() => onDismiss(row.id)}>
            {t('imports:amendments.dismiss')}
          </Button>
        </div>
      ))}
    </ListItem>
  );
};

export type RemovedRow = LineSnapshot & { id: string };

export type LineListProps = {
  lines: readonly MarkdownLine[];
  filter: string;
  originals: Readonly<Record<string, LineSnapshot>>;
  selectedId: string | null;
  selectionOrigin: 'list' | 'source' | null;
  inlineAmendments: Readonly<Record<string, readonly AmendmentRow[]>>;
  disabled: boolean;
  onSelect: (id: string | null) => void;
  onShowOnly: (key: string) => void;
  onOpenSource?: () => void;
  onChange: (id: string, value: string) => void;
  onReset: (id: string) => void;
  onRemove: (line: MarkdownLine) => void;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
};

export const LineList = ({
  lines, filter, originals, selectedId, selectionOrigin, inlineAmendments, disabled,
  onSelect, onShowOnly, onOpenSource, onChange, onReset, onRemove, onAccept, onDismiss,
}: LineListProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const visible = useMemo(() => lines.filter((line) => matchesFilter(line, filter)), [lines, filter]);
  const groups = useMemo(() => groupByFamily(visible), [visible]);
  const flagged = useMemo(() => visible.filter((line) => FLAGGED.has(line.status)), [visible]);
  const selectedLine = lines.find((line) => line.id !== null && line.id === selectedId) ?? null;

  // The selected row stays in view however the selection was made.
  useEffect(() => {
    if (selectedId === null) return;
    const element = document.getElementById(`import-row-${selectedId}`);
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [selectedId]);

  const jumpToNextFlagged = () => {
    if (flagged.length === 0) return;
    const index = flagged.findIndex((line) => line.id !== null && line.id === selectedId);
    const next = flagged[(index + 1) % flagged.length];
    onSelect(next.id);
  };

  const renderRow = (line: MarkdownLine) => (
    <LineRow
      key={line.id ?? `line-${line.lineNo}`}
      line={line}
      original={line.id ? originals[line.id] : undefined}
      selected={line.id !== null && line.id === selectedId}
      focusInput={selectionOrigin === 'source' && line.id !== null && line.id === selectedId}
      amendments={line.id ? inlineAmendments[line.id] ?? [] : []}
      disabled={disabled}
      onSelect={onSelect}
      onChange={onChange}
      onReset={onReset}
      onRemove={onRemove}
      onAccept={onAccept}
      onDismiss={onDismiss}
      onShowOnly={onShowOnly}
      onOpenSource={onOpenSource}
    />
  );

  return (
    <section aria-label={t('imports:editor.lines')}>
      <div className={classes.summary}>
        <Typography variant="body2" className={classes.muted}>
          {t('imports:rows.summary', { lines: lines.length, flagged: flagged.length })}
        </Typography>
        <Button size="small" className={classes.button} disabled={flagged.length === 0} onClick={jumpToNextFlagged}>
          {t('imports:rows.nextFlagged')}
        </Button>
      </div>
      <div role="status" aria-live="polite" className={classes.srOnly}>
        {selectionOrigin === 'source' && selectedLine
          ? t('imports:links.announce', { key: selectedLine.key, page: selectedLine.src?.page ?? 0, line: selectedLine.src?.line ?? '' })
          : ''}
      </div>
      {lines.length === 0 || visible.length === 0 ? (
        <Typography variant="body2" className={classes.empty}>
          {t(lines.length === 0 ? 'imports:editor.noLines' : 'imports:filter.empty')}
        </Typography>
      ) : (
        <List disablePadding>
          {groups.map((group) => (
            <li key={group.family}>
              <ul style={{ padding: 0 }}>
                <ListSubheader disableSticky className={classes.group}>
                  {t(`imports:groups.${group.family}`, { defaultValue: group.family })}
                </ListSubheader>
                {group.lines.map(renderRow)}
              </ul>
            </li>
          ))}
        </List>
      )}
    </section>
  );
};

export type RemovedRowsProps = {
  removed: readonly RemovedRow[];
  disabled: boolean;
  onRestore: (id: string) => void;
};

/** Rows taken out of the markdown; each one returns to its original position when restored. */
export const RemovedRows = ({ removed, disabled, onRestore }: RemovedRowsProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <List disablePadding>
      {removed.map((row) => (
        <ListItem key={row.id} disableGutters className={classes.removedRow}>
          <Typography variant="body2" className={`${classes.muted} ${classes.removedText}`}>{`${humanKeyLabel(row.key)}: ${row.value}`}</Typography>
          <Button size="small" className={`${classes.button} ${classes.restore}`} disabled={disabled} onClick={() => onRestore(row.id)}>
            {t('imports:rows.restore')}
          </Button>
        </ListItem>
      ))}
    </List>
  );
};
